<template>
    <div v-if="props.currentSubscription" class="subscription-form">
        <div
            v-for="plan in planCollection.data.value"
            :key="plan.id.value"
        >
            <div class="subscription">
                <div
                    v-if="props.currentSubscription.plan.equals(plan)"
                    class="subscription__active-label"
                >
                    <chip-informative :color="'green-500'">
                        <text-decorator weight="bold">
                            Actief abonnement
                        </text-decorator>
                    </chip-informative>
                </div>
                <div
                    v-if="form.plan?.equals(plan)"
                    class="subscription__active-label"
                >
                    <chip-informative :color="'primary'">
                        <text-decorator weight="bold">
                            Geselecteerd abonnement
                        </text-decorator>
                    </chip-informative>
                </div>
                <div class="subscription__header">
                    <div>
                        <text-decorator
                            :size="FontSize.xl"
                            weight="bold"
                        >
                            {{ formatMoney(plan.price, { maximumFractionDigits: 0 }) }}
                        </text-decorator>
                        <text-decorator
                            :color="'gray-500'"
                            :size="FontSize.xs"
                            weight="bold"
                        >
                            / maand
                        </text-decorator>
                    </div>
                    <div>
                        <text-decorator :size="FontSize.lg">
                            {{ plan.name }}
                        </text-decorator>
                    </div>
                    <div>
                        <text-decorator :size="FontSize.xs">
                            {{ plan.description }}
                        </text-decorator>
                    </div>
                </div>

                <winck-subscription-usps
                    v-if="plan.winckPlanAddon !== null"
                    :plan="plan"
                />

                <div
                    v-if="props.currentSubscription.plan.equals(plan)"
                    class="subscription__actions"
                >
                    <div>
                        <text-decorator
                            :color="'green-500'"
                            :size="FontSize.xs"
                            weight="bold"
                        >
                            Je abonnement wordt verlengd op:<br>
                            {{ formatDate(props.currentSubscription.endDate) }}
                        </text-decorator>
                    </div>
                    <div v-if="props.currentSubscription.discount">
                        <horizontal-line />
                        <text-decorator
                            :color="'green-500'"
                            :size="FontSize.xs"
                        >
                            Je ontvangt een korting van:<br> €{{ props.currentSubscription.discount.amount }},- per maand
                            <template v-if="props.currentSubscription.discount.endDate">
                                <br>tot {{ formatDate(props.currentSubscription.discount.endDate) }}
                            </template>
                        </text-decorator>
                    </div>
                </div>

                <div
                    v-if="plan.price < props.currentSubscription.plan.price"
                    class="subscription__actions"
                >
                    <button-block v-tooltip.info="'Downgraden? Neem contact op met je accountmanager.'" disabled>
                        Downgrade
                    </button-block>
                </div>

                <div
                    v-if="plan.price > props.currentSubscription.plan.price"
                    class="subscription__actions"
                >
                    <template v-if="!form.plan?.equals(plan)">
                        <button-outline @click="form.plan = plan">
                            Selecteer
                        </button-outline>
                    </template>
                    <template v-else>
                        <button-outline @click="form.plan = null">
                            Deselecteer
                        </button-outline>
                    </template>
                </div>
            </div>
        </div>
    </div>

    <flex-container justify-content="flex-end" align-items="center">
        <slot name="actions" />
    </flex-container>
</template>

<script setup lang="ts">
    import { FontSize } from '@keyvue/one-ui';
    import WinckSubscriptionUsps from '@/subscription/subscription/components/WinckSubscriptionUsps.vue';
    import { formatDate } from '@/shared/utils/date';
    import { formatMoney } from '@/shared/utils/money';
    import { ref } from 'vue';
    import type { ReactiveCollection } from '@/services/keyvue-api/composables/useClient';
    import type { Plan, Subscription } from '@keyvue/one-api-client';

    export interface Props {
        currentSubscription: Subscription
        planCollection: ReactiveCollection<Plan>
    }

    export interface Form {
        plan: Plan | null,
    }

    export interface ExposedSubscriptionsForm {
        form: Form,
        setForm: (values: Form) => void,
    }

    const props = defineProps<Props>();

    const form = ref<Form>({
        plan: null,
    });

    function setForm(values: Form) {
        form.value.plan = values.plan;
    }

    defineExpose({
        form,
        setForm,
    });
</script>

<style scoped lang="scss">
    .subscription-form {
        width: 100%;
        max-width: 100%;
        box-sizing: border-box;
        padding-top: size(md);
        display: flex;
        gap: size(md);

        & > div {
            flex-grow: 1;
        }
    }

    .subscription {
        position: relative;
        border-radius: size(sm);
        border: 1px solid color(gray-300);

        &__active-label {
            position: absolute;
            width: max-content;
            left: 50%;
            transform: translateX(-50%);
            top: -11px;
        }

        &__header {
            border-bottom: 1px solid color(gray-300);
            display: flex;
            flex-direction: column;
            padding: size(lg) size(md) size(md);
            gap: size(xs);
        }

        &__actions {
            border-top: 1px solid color(gray-300);
            padding: size(md);
            text-align: center;
        }
    }
</style>
